import React, { useState } from 'react';
import { Form, Button, message } from "antd";
import { useTranslation } from 'react-i18next';
import ResponsiveModal from '../../Shared/ResponsiveModal/ResponsiveModal';
import ChannelForm from '../ChannelForm/ChannelForm';
import CredentialForm from '../../Credentials/CredentialForm/CredentialForm';
import { createCredential } from '../../Credentials/CredentialsServices';
import { useErrorHandler } from '../../../Hooks/useErrorHandler';

// We need an internal component for the Credential Form since AddCredentialModal includes the modal wrapper
// If AddCredentialModal is tightly coupled with the Modal, we might need to extract the form.
// Checking imports, it seems AddCredentialModal is the export. 
// For now, let's assume we can reuse AddCredentialModal's inner logic or we need to extract it. 
// UPDATE: To "hide show in the same modal", we should switch the CONTENT of the ResponsiveModal.

const AddChannelModal = ({ onCancel, onCreate, onUpdate, isEdit = false, channel = null, loading = false }) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [view, setView] = useState('channel'); // 'channel' | 'credential'
    const [credentialLoading, setCredentialLoading] = useState(false);
    const { handleError } = useErrorHandler();

    // For the credential form
    const [credentialForm] = Form.useForm();

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            let success;
            if (isEdit && onUpdate && channel) {
                success = await onUpdate(channel.id, values);
            } else if (onCreate) {
                success = await onCreate(values, form);
            }

            if (success) {
                form.resetFields();
                onCancel();
            }
        } catch (error) {
            if (error.errorFields) return;
            handleError(error);
        }
    };

    const handleCreateCredential = async () => {
        try {
            const values = await credentialForm.validateFields();
            setCredentialLoading(true);
            const response = await createCredential(values);
            message.success(t('credential_created_successfully'));

            // Switch back to channel view
            setView('channel');
            credentialForm.resetFields();

            // Pre-select the new credential in the channel form
            if (response?.data?.id) {
                // We need to ensure the ChannelForm re-fetches credentials or we update its list.
                // Since ChannelForm fetches on mount/update, switching view might re-mount it depending on implementation.
                // To be safe and ensure the option exists, we might need to manually trigger a refetch in ChannelForm 
                // or optimistically add it. For now, setting the value.
                form.setFieldsValue({ credential_id: response.data.id });

                // Ideally ChannelForm exposure to refetch credentials would be better, 
                // but re-rendering it typically fetches the list.
            }
        } catch (error) {
            if (error.errorFields) return; // Validation error
            handleError(error);
        } finally {
            setCredentialLoading(false);
        }
    };

    // Footer for Channel View
    const channelFooter = (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button onClick={onCancel}>
                {t('cancel')}
            </Button>
            <Button
                type="primary"
                onClick={handleSubmit}
                loading={loading}
            >
                {isEdit ? t('save') : t('add')}
            </Button>
        </div>
    );

    // Footer for Credential View
    const credentialFooter = (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button onClick={() => {
                setView('channel');
                credentialForm.resetFields();
            }}>
                {t('back')}
            </Button>
            <Button
                type="primary"
                onClick={handleCreateCredential}
                loading={credentialLoading}
            >
                {t('create_credential')}
            </Button>
        </div>
    );

    return (
        <ResponsiveModal
            title={view === 'channel' ? (isEdit ? t('edit_channel') : t('add_channel')) : t('create_new_credential')}
            open
            onCancel={onCancel}
            desktopWidth={600}
            footer={view === 'channel' ? channelFooter : credentialFooter}
        >
            {view === 'channel' ? (
                <ChannelForm
                    form={form}
                    isEdit={isEdit}
                    initialValues={channel}
                    onCreateCredentialRequest={() => setView('credential')}
                />
            ) : (
                // We import AddCredentialForm directly if possible, otherwise we replicate the form structure here
                // Since AddCredentialModal contains the modal wrapper, we should check if there is an exported Form component.
                // If not, we might need to import AddCredentialModal and check if it can render without modal, 
                // OR we just use a generic Form here for credentials. 
                // Let's check AddCredentialForm file existence in next step if this fails, but for now assuming we need to render the fields manually or use a shared component.
                // Reusing the fields from the earlier seen form structure in AddCredentialModal (implied)
                // Actually, I'll use the AddCredentialModal content if I can access it. 
                // Wait, I haven't seen AddCredentialForm.js independently. I should check if it exists.
                // For safety, I will implement a minimal Credential Form here or import it if I find it.
                // I'll check for AddCredentialForm existence first.
                <CredentialForm form={credentialForm} />
            )}
        </ResponsiveModal>
    );
};

export default AddChannelModal;
